import _                                           from 'underscore';
import classNames                                  from 'classnames';
import React, { FC, useContext, useRef, useState } from 'react';
import { Button, Form, Icon, Segment }             from 'semantic-ui-react';
import { Cell, Grid }                              from 'styled-css-grid';
import { Formik, FormikConfig, FormikProps }       from 'formik';
import * as Yup                                    from 'yup';

import { CorvalProjectActivity, CorvalProjectSubObjective, CorvalStaticProjectActivity, CorvalUserLike, GQLNode, Size, Uuid } from '../../../types';

import { DecisionEngineQueries }                                                  from '../../../queries';
import { useAuditRecords, useCurrentUser, useProjectActivities, useSystemToasts } from '../../../hooks';
import { SystemRecordQueries }                                                    from '../../../queries/SystemRecordQueries';
import { EMPTY_VALUE_DISPLAY }                                                    from '../../../constants/FormatStringEnums';
import { AUDIT_RECORD_TYPE }                                                      from '../../../constants/SystemRecordEnums';
import { ProjectContext }                                                         from '../../pages/projects/ProjectIndexPage';
import { StyledContainer }                                                        from '../../containers';
import { LastUpdatedAuditTag }                                                    from '../audits/LastUpdatedAuditTag';
import { ItemFromQuery, ItemFromQueryProps }                                      from '../from-query/ItemFromQuery';
import { TextLink }                                                               from '../display/TextLink';
import { UserAvatar }                                                             from '../user-avatar/UserAvatar';
import { TextAreaField }                                                          from '../forms/formik';
import { useMutation }                                                            from '@apollo/react-hooks';
import { QueryResult }                                                            from '@apollo/react-common';
import { AccountUser }                                                            from '../user/AccountUser';
import { FinePrintMessage }                                                       from '../util/messaging/FinePrintMessage';
import { InlineLoadingIndicatorLabel }                                            from '../util/loading/InlineLoadingIndicatorLabel';
import { StringOrEmpty }                                                          from '../display/StringOrEmpty';
import { ProjectQueries }                                                         from "@src/queries/ProjectQueries";


export type EntityNoteType =
    'projectObjective' |
    'projectActivity' |
    'staticProjectActivity'

interface Props {
    id: Uuid,
    type: EntityNoteType
    editable?: boolean,
    showLastEditedOnNote?: boolean,
    projectId?: Uuid,
    projectVersionId?: Uuid,
    containerComponent?: FC
}

const defaultProps: Props = {
    id:                   null,
    type:                 null,
    editable:             true,
    showLastEditedOnNote: false,
    projectId:            null,
    projectVersionId:     null,
    containerComponent:   ( props ) => <Segment size={ 'small' } secondary basic={ true } style={ { padding: `10px 5px`, borderRadius: 4 } }>{ props.children }</Segment>
}

type EntityNoteHashInfoType = Record<EntityNoteType, {
    auditType: AUDIT_RECORD_TYPE,
    requestOptions: ItemFromQueryProps,
    mutation: GQLNode,
    mutationOptionsFunc: any,
}>

interface EntityNoteEditorFormValues {
    notes: string,
}

const EntityNote: FC<Props> = ( props ) => {

    let { projectId }                                      = props.projectId ? props : useContext( ProjectContext ),
        { projectVersionId }                               = props.projectVersionId ? props : useContext( ProjectContext ),
        { currentUser }                                    = useCurrentUser(),
        { getAuditRecordTypes, getAuditRecordsFromEntity } = useAuditRecords(),
        { getUpdateProjectActivityInputData }              = useProjectActivities(),
        noteReqRef                                         = useRef<QueryResult>(),
        toastActions                                       = useSystemToasts(),
        formikRef                                          = useRef<FormikProps<EntityNoteEditorFormValues>>(),
        [ isEditing, setIsEditing ]                        = useState( false );

    let noteTypeHash: EntityNoteHashInfoType = {
        'projectObjective':      {
            auditType:           AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_NOTE_CHANGED,
            requestOptions:      {
                query:             DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES( `
					${ DecisionEngineQueries.fragments.PROJECT_SUBOBJECTIVES.QUERY_RETURN( projectId ) } 
					${ SystemRecordQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
				`, projectVersionId ),
                queryOptions:      {
                    variables:   {
                        projectId,
                        projectVersionId,
                        where: { id: { eq: props.id } }
                    },
                    fetchPolicy: 'network-only',
                },
                queryResultObject: ( data: { projectSubObjectives: CorvalProjectSubObjective[] } ) => {
                    return _.first( data.projectSubObjectives ) ?? null;
                },
            },
            mutation:            DecisionEngineQueries.mutations.UPDATE_PROJECT_SUBOBJECTIVES( `id ${ SystemRecordQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }` ),
            mutationOptionsFunc: ( values: any ) => {

                let data       = _.first( noteReqRef.current.data.projectSubObjectives ),
                    updateData = _.pick( data, [
                        'id'
                    ] );

                return ( {
                    variables: {
                        input: {
                            projectSubObjectives: [
                                {
                                    ...updateData,
                                    ...values,
                                },
                            ]
                        }
                    }
                } );
            }
        },
        'projectActivity':       {
            auditType:           AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_NOTE_CHANGED,
            requestOptions:      {
                query:             DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
					${ DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS_VITAL } 
					${ SystemRecordQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
				 `, projectVersionId ),
                queryOptions:      {
                    variables:   {
                        projectId,
                        projectVersionId,
                        where: { id: { eq: props.id } }
                    },
                    fetchPolicy: 'network-only',
                },
                queryResultObject: ( data: { projectActivites: CorvalProjectActivity[] } ) => {
                    return _.first( data.projectActivites ) ?? null;
                },
            },
            mutation:            DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES( `id ${ SystemRecordQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }` ),
            mutationOptionsFunc: ( values: any ) => {

                let projectActivity = _.first( noteReqRef.current.data.projectActivites );

                // other required PA data
                let updateData = getUpdateProjectActivityInputData( projectActivity )

                return ( {
                    variables: {
                        input: {
                            projectId:         projectId,
                            projectActivities: [
                                {
                                    ...updateData,
                                    ...values,
                                },
                            ]
                        }
                    }
                } );
            }
        },
        'staticProjectActivity': {
            auditType:           AUDIT_RECORD_TYPE.STATIC_PROJECT_ACTIVITY_NOTE_CHANGED,
            requestOptions:      {
                query:             DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `id notes`, projectVersionId ),
                queryOptions:      {
                    variables:   {
                        projectId,
                        projectVersionId,
                        where: { id: { eq: props.id } }
                    },
                    fetchPolicy: 'network-only',
                },
                queryResultObject: ( data: { staticProjectActivites: CorvalStaticProjectActivity[] } ) => {
                    return _.first( data.staticProjectActivites ) ?? null;
                },
            },
            mutation:            ProjectQueries.mutations.UPDATE_PROJECT_VERSION_DATA(),
            mutationOptionsFunc: ( values: any ) => {

                let staticProjectActivity = _.first( noteReqRef.current.data.staticProjectActivites );

                return ( {
                    variables: {
                        input: {
                            projectVersionId: projectVersionId,
                            activityChanges:  {
                                modified: [
                                    {
                                        id:    staticProjectActivity.id,
                                        notes: values.notes,
                                    },
                                ]
                            }
                        }
                    }
                } );
            }
        },
    }

    if( !props.id || !props.type ) return <>{ EMPTY_VALUE_DISPLAY }</>;

    let { requestOptions, auditType }     = noteTypeHash[ props.type ],
        { mutation, mutationOptionsFunc } = noteTypeHash[ props.type ],
        [ mutationFunc, mutationFuncReq ] = useMutation( mutation );

    const handleToggleContainer = () => {

        setIsEditing( !isEditing );
    }

    const handleSubmission: FormikConfig<EntityNoteEditorFormValues>['onSubmit'] = async ( values, form ) => {

        try{

            await mutationFunc( mutationOptionsFunc( values ) ).then( ( response ) => {

                if( response ){

                    toastActions.success( `Notes saved successfully!` );

                    noteReqRef.current.refetch();

                } else {

                    toastActions.error( 'Mutation Error', mutationFuncReq.error.message, { timeoutMS: 0 } )
                }

            } );

        } catch( e ) {

            toastActions.error( 'Network Error', `${ e.message }` );
        }
    };

    return (

        <ItemFromQuery
            queryRef={ noteReqRef }
            noItemValid={ true }
            loadingComponent={ () => (
                <StyledContainer padding={ Size.XS }>
                    <InlineLoadingIndicatorLabel label={ 'Loading notes...' }/>
                </StyledContainer>
            ) }
            { ...requestOptions }>

            { ( noteEntity: any ) => {

                let auditEntries     = getAuditRecordsFromEntity( noteEntity ) ?? [],
                    noteAuditEntries = getAuditRecordTypes( auditEntries, [ auditType ], 'newest' ),
                    noAuditEntries   = _.isEmpty( noteAuditEntries ),
                    notes            = noteEntity?.notes ?? null,
                    noteIsBlank      = _.isEmpty( notes ? notes.trim() : '' ),
                    noteIsEmpty      = noAuditEntries && noteIsBlank;

                const lastEditorUserId = null; // currentUser.id;

                let ContainerComponent = props.containerComponent;

                return (

                    <ContainerComponent>

                        {/* Normal notes */ }
                        { !props.editable && !noteIsBlank && (
                            <Grid columns={ `auto 1fr` } rows={ `auto auto` }>
                                <Cell width={ 2 }>
                                    <StringOrEmpty evaluate={ notes } emptyValue={ '' }/>
                                </Cell>
                                { props.showLastEditedOnNote && (
                                    <Cell>
                                        <LastUpdatedAuditTag records={ noteAuditEntries } useIcon={ false } usePopup={ false }/>
                                    </Cell>
                                ) }
                            </Grid>
                        ) }

                        {/* Editable notes */ }
                        { props.editable && (

                            <>

                                <Grid columns={ `auto 1fr auto` } columnGap={ '10px' } className={ classNames( {
                                    'peek-container':     true,
                                    'position--absolute': isEditing,
                                    'z-index--100':       isEditing,
                                } ) } style={ !isEditing ? {} : {
                                    top:   15,
                                    right: 10,
                                } }>

                                    {/* Last user to add notes avatar */ }
                                    { !( noteIsEmpty || noteIsBlank ) && (
                                        <Cell center>
                                            { !isEditing && !_.isNull( lastEditorUserId ) && (
                                                <AccountUser accountId={ lastEditorUserId } loadingLabel={ '' } as={ React.Fragment }>

                                                    { ( user: CorvalUserLike ) => (
                                                        <UserAvatar user={ user }
                                                                    size={ 'nano' }
                                                                    asInitials={ true }
                                                                    onClick={ handleToggleContainer }/>
                                                    ) }

                                                </AccountUser>
                                            ) }
                                        </Cell>
                                    ) }

                                    {/* Actual notes */ }
                                    <Cell width={ ( noteIsEmpty || noteIsBlank ) ? 2 : 1 }>

                                        { !isEditing && (
                                            <TextLink as={ 'p' } className={ 'fade-in' } onClick={ handleToggleContainer }>

                                                { ( noteIsEmpty || noteIsBlank ) ? (

                                                    /* Let uset know thy can add a notes */
                                                    <FinePrintMessage as={ 'span' } className={ 'display--inline-block margin-sm-left' } fadeIn={ false }>Add notes...</FinePrintMessage>

                                                ) : (

                                                      /* Otherwise show notes */
                                                      <Grid columns={ `auto 1fr` } rows={ `auto auto` }>
                                                          <Cell width={ 2 }>
                                                              <span style={ { whiteSpace: 'break-spaces' } }>{ notes }</span>
                                                          </Cell>

                                                          { props.showLastEditedOnNote && (
                                                              <Cell>
                                                                  <LastUpdatedAuditTag records={ noteAuditEntries } useIcon={ false } usePopup={ false }/>
                                                              </Cell>
                                                          ) }

                                                      </Grid>
                                                  ) }

                                            </TextLink>
                                        ) }
                                    </Cell>

                                    {/* Displayable edit icon */ }
                                    <Cell center className={ 'display--grid' }>

                                        <TextLink as={ 'span' } className={ classNames( {
                                            'fade-in':   isEditing,
                                            'peek-item': !isEditing,
                                        } ) }>

                                            <Icon onClick={ handleToggleContainer } name={ isEditing ? 'close' : 'pencil' } color={ 'grey' }/>

                                        </TextLink>

                                    </Cell>
                                </Grid>

                                { isEditing && (

                                    <Formik<EntityNoteEditorFormValues>
                                        innerRef={ formikRef }
                                        initialValues={ { notes } }
                                        enableReinitialize={ true }
                                        validateOnChange={ true }
                                        validateOnBlur={ true }
                                        validationSchema={ Yup.object( {
                                            notes: Yup.string().nullable().max( 4000, 'Notes field has a max character limit of 4000.' ),
                                        } ) }
                                        onSubmit={ handleSubmission }>

                                        { formik => {

                                            return (
                                                <Form className={ 'fade-in padding-xs' }>

                                                    <Grid columns={ `1fr auto` }>
                                                        <Cell width={ 2 }>

                                                            <TextAreaField
                                                                rows={ 10 }
                                                                label={ 'Update notes' }
                                                                style={ { fontSize: 12 } }
                                                                name={ 'notes' }
                                                                disabled={ formik.isSubmitting }
                                                                placeholder={ 'Provide details on this item for your team.' }
                                                                onChange={ ( e, data ) => {

                                                                    formik.setFieldValue( 'notes', data.value, true );

                                                                } }
                                                            />

                                                        </Cell>
                                                        <Cell left={ 2 } width={ 1 }>

                                                            <Button basic
                                                                    type={ 'text' }
                                                                    size={ 'tiny' }
                                                                    color={ 'grey' }
                                                                    content={ 'Close' }
                                                                    disabled={ !formik.isValid || formik.isSubmitting }
                                                                    onClick={ handleToggleContainer }/>

                                                            <Button primary
                                                                    type={ 'text' }
                                                                    size={ 'tiny' }
                                                                    content={ 'Save' }
                                                                    loading={ formik.isSubmitting }
                                                                    disabled={ !formik.isValid || formik.isSubmitting }
                                                                    onClick={ e => formik.submitForm() }/>


                                                        </Cell>
                                                    </Grid>
                                                </Form>
                                            )
                                        } }
                                    </Formik>
                                ) }


                            </>

                        ) }


                    </ContainerComponent>
                )
            } }
        </ItemFromQuery>
    );
};

EntityNote.defaultProps = defaultProps;

export { EntityNote }
